import { OwcChip, OwcGrid, OwcTooltip, OwcTypography } from "@one/react";
import CustomChip from "../CustomChip";
import { EQUIPMENT_DETAILS_HEADING } from "../../../constants";
import { valueOrEmpty } from "../../../utils/helpers/text";
import { useEffect, useState } from "react";
import { withApollo } from "react-apollo";
import { BOOKING_GET_CHECKLISTS_FOR_EQUIPMENTS } from "../../../gql/bookingapi";
import ChecklistFolder from "../../../icons/ChecklistFolder";
import BookingAccessControlRender from "../../../features/equipment-list/BookingAccessControlRender";
import { visibleAttributeKeys } from "../../../utils/helpers/vnvEquipment";

const REPOSITORY_URL = process.env.REACT_APP_REPOSITORY_URL || "";

const PopoverAttributesDetails = ({
  data,
  client,
  title,
  metaData,
}) => {
  const [loading, setLoading] = useState(false);
  const [hasChecklists, setHasChecklists] = useState(false);

  useEffect(() => {
    if (!data?.siteName || !data?.belongingToGroup || !data?.equipmentModel) {
      return;
    }
    const equipmentKey = `${data.siteName}#${data.belongingToGroup}#${data.equipmentModel}`;

    setLoading(true);
    client
      .query({
        query: BOOKING_GET_CHECKLISTS_FOR_EQUIPMENTS,
        variables: { keys: [equipmentKey] },
        fetchPolicy: "no-cache",
      })
      .then((res) => {
        const entries = res?.data?.getChecklistsForEquipments || [];
        const eqChecklists = entries?.[0]?.checklists || [];
        setHasChecklists(eqChecklists.length > 0);
      })
      .catch((error) => console.error("Checklist fetch error:", error))
      .finally(() => setLoading(false));
  }, [data?.siteName, data?.belongingToGroup, data?.equipmentModel, client]);

  const handleViewDocumentation = () => {
    if (REPOSITORY_URL) {
      const inventoryId = data?.inventoryId || data?.id;
      window.open(`${REPOSITORY_URL}/equipmentList/${inventoryId}?action=documentation`, "_blank");
    }
  };

  const displayDataInChipsOrText = (key, equipmentData) => {
    if (key === "checklists") {
      if (loading || !hasChecklists) {
        return "-";
      }
      const shortId = equipmentData?.shortIdentifier || "-";

      return (
        <button
          onClick={handleViewDocumentation}
          style={{
            background: "none",
            border: "none",
            cursor: "pointer",
            padding: 0,
            display: "inline-flex",
            alignItems: "center",
            whiteSpace: "nowrap",
            marginTop: "4px",
          }}
          title={`View documentation for ${shortId}`}
        >
          <ChecklistFolder size={20} />
          &nbsp;
          <OwcTypography variant="button" style={{ fontWeight: 600 }}>
            {shortId}
          </OwcTypography>
        </button>
      );
    }

    if (key === "qualificationStatus" || key === "systemStatus") {
      let value = valueOrEmpty(equipmentData[key], false, "-");
      return value !== "-" ? (
        <div>
          <OwcChip outlined id={`attr_${value}`}>
            {value}{" "}
          </OwcChip>
          <OwcTooltip anchor={`attr_${value}`}>{value}</OwcTooltip>
        </div>
      ) : (
        value
      );
    } else {
      return (
        <div>
          <OwcTypography variant="button" id={`attr_${key}`}>
            {valueOrEmpty(equipmentData[key], false, "-")}
          </OwcTypography>
          <OwcTooltip anchor={`attr_${key}`}>{valueOrEmpty(equipmentData[key], false, "-")}</OwcTooltip>
        </div>
      );
    }
  };

  const renderAccessControlValue = () => {
    if (!data) {
      return "-";
    }
    return <BookingAccessControlRender data={data} />;
  };

  // V&V equipment swaps Nickname and Manufacturer, which P1S V&V records never carry, for V&V
  // Status. Filtered before the access control splice so the checklist index stays correct.
  const rawMetaKeys = visibleAttributeKeys(Object.keys(metaData || {}), data);
  const checklistIndex = rawMetaKeys.indexOf("checklists");
  const shouldShowAccessControl = Boolean(data);
  const orderedMetaKeys = [...rawMetaKeys];

  if (shouldShowAccessControl) {
    if (checklistIndex >= 0) {
      orderedMetaKeys.splice(checklistIndex + 1, 0, "__accessControl__");
    } else {
      orderedMetaKeys.push("__accessControl__");
    }
  }

  return (
    <div style={{ padding: "0 1rem" }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: '8px', margin: '1.125rem 0' }}>
        <OwcTypography variant="button">
          {title}
        </OwcTypography>
        {(() => {
          if (title !== EQUIPMENT_DETAILS_HEADING) {
            return null;
          }
          let booked = Array.isArray(data?.bookedEquipments) ? data.bookedEquipments : null;
          if (!booked || booked.length === 0) {
            if (Array.isArray(data?.bookingEntries)) {
              booked = Array.from(new Set(
                data.bookingEntries.map((entry) => (entry?.equipment?.shortIdentifier || entry?.resource)).filter(Boolean)
              ));
            }
          }
          if (booked && booked.length >= 1) {
            return (
              <CustomChip
                text={`${booked.length}`}
                color="#fff"
                bgColor="rgba(11, 65, 205, 1)"
                compact
                info="Total items in this booking"
              />
            );
          }
          return null;
        })()}
      </div>

      <OwcGrid container columns="4" vGutter={{ m: 10, l: 15, xl: 20 }}>
        {orderedMetaKeys.map((key) => {
          if (key === "__accessControl__") {
            return (
              <OwcGrid item l={1} m={1} xs={1} s={1} key="popover_access_control" style={{ marginBottom: 16 }}>
                <OwcTypography variant="caption">Access control</OwcTypography>
                <span>{renderAccessControlValue()}</span>
              </OwcGrid>
            );
          }

          return (
            <OwcGrid item l={1} m={1} xs={1} s={1} key={metaData[key].key} style={{ marginBottom: 16 }}>
              <OwcTypography variant="caption">{metaData[key].value}</OwcTypography>
              <span
                style={{
                  display: "-webkit-box",
                  WebkitLineClamp: 1,
                  WebkitBoxOrient: "vertical",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                }}
              >
                {displayDataInChipsOrText(key, data)}
              </span>
            </OwcGrid>
          );
        })}
      </OwcGrid>
    </div>
  );
};

export default withApollo(PopoverAttributesDetails);
